import { useState, useMemo } from 'react';
import { useRepricingSuggestions, useApplyRepricing } from '@/hooks/useProducts';
import { RepricingSuggestion } from '@/types';
import Button from '@/components/ui/Button';
import { TrendingUp, Banknote, AlertTriangle, RefreshCw, Check } from 'lucide-react';
import clsx from 'clsx';
import {
  Unit,
  UNIT_LABEL_SHORT,
  pricePerDisplayFromBase,
  pricePerBaseFromDisplay,
} from '@/utils/units';

/**
 * Convierte precio en base_unit (lo que viene del backend) a display_unit (lo que ve el usuario).
 * Ej: producto en peso, base=gram, display=kilogram → multiplica por 1000.
 */
function toDisplay(value: number | null | undefined, displayUnit: Unit | undefined): number | null {
  if (value == null) return null;
  const u = displayUnit || 'unit';
  return pricePerDisplayFromBase(Number(value), u);
}

function toBase(value: number, displayUnit: Unit | undefined): number {
  const u = displayUnit || 'unit';
  return pricePerBaseFromDisplay(value, u);
}

const fmtMoney = (v?: number | null) =>
  v == null ? '—' : `$${Number(v).toLocaleString('es-AR', { maximumFractionDigits: 2 })}`;

const fmtPct = (v?: number | null) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

interface EditedPrices {
  cash?: string;
  card?: string;
  transfer?: string;
  qr?: string;
}

export default function Repricing() {
  const { data: suggestions, isLoading, refetch, isFetching } = useRepricingSuggestions();
  const applyMutation = useApplyRepricing();
  const [edits, setEdits] = useState<Record<number, EditedPrices>>({});
  const [appliedIds, setAppliedIds] = useState<Set<number>>(new Set());

  const totalCount = suggestions?.length ?? 0;
  const criticalCount = useMemo(
    () =>
      suggestions?.filter(
        (s) => s.actual_margin != null && s.margin_min != null && Number(s.actual_margin) < Number(s.margin_min) - 10,
      ).length ?? 0,
    [suggestions],
  );

  const handleEdit = (productId: number, field: keyof EditedPrices, value: string) => {
    setEdits((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value },
    }));
  };

  /** Devuelve el valor (en display_unit) que debería enviarse, basado en edición o sugerido. */
  const valOrSuggestedDisplay = (s: RepricingSuggestion, field: keyof EditedPrices): number | undefined => {
    const edit = edits[s.product_id]?.[field];
    if (edit !== undefined && edit !== '') return Number(edit);
    if (edit === '') return undefined;
    const map = {
      cash: s.suggested_cash,
      card: s.suggested_card,
      transfer: s.suggested_transfer,
      qr: s.suggested_qr,
    };
    const baseVal = map[field];
    return baseVal != null ? toDisplay(Number(baseVal), s.display_unit as Unit) ?? undefined : undefined;
  };

  const handleApply = async (s: RepricingSuggestion) => {
    try {
      const unit = s.display_unit as Unit | undefined;
      const cashD = valOrSuggestedDisplay(s, 'cash');
      const priceBase = cashD != null ? toBase(cashD, unit) : undefined;
      // Precio único: enviamos el mismo valor a todas las columnas (el backend igual sincroniza)
      await applyMutation.mutateAsync({
        productId: s.product_id,
        payload: {
          cash: priceBase,
          card: priceBase,
          transfer: priceBase,
          qr: priceBase,
          reason: 'Repricing por margen objetivo',
        },
      });
      setAppliedIds((prev) => new Set(prev).add(s.product_id));
    } catch (e) {
      console.error('Error applying repricing:', e);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-taupe-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-taupe-700" />
            Repricing — Productos con margen erosionado
          </h1>
          <p className="text-taupe-500 mt-1 text-sm">
            Productos cuyo margen real (efectivo) cayó por debajo del mínimo configurado en la categoría o el override.
          </p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} loading={isFetching}>
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Recalcular
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs uppercase tracking-wide text-taupe-600 font-semibold">Productos a repricar</p>
          <p className="text-3xl font-bold text-taupe-900 mt-1">{totalCount}</p>
        </div>
        <div className="card">
          <p className="text-xs uppercase tracking-wide text-taupe-600 font-semibold">Críticos (10%+ por debajo)</p>
          <p className="text-3xl font-bold text-rose-600 mt-1">{criticalCount}</p>
        </div>
        <div className="card bg-cream-50 border border-cream-200">
          <p className="text-xs uppercase tracking-wide text-taupe-700 font-semibold flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            Cómo funciona
          </p>
          <p className="text-xs text-taupe-700 mt-1">
            La sugerencia aplica el <strong>margen objetivo</strong> al precio de venta a partir del costo final.
          </p>
        </div>
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="card text-center py-12">Cargando...</div>
      ) : !suggestions || suggestions.length === 0 ? (
        <div className="card text-center py-12">
          <Check className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <p className="text-taupe-700 font-medium">No hay productos con margen bajo</p>
          <p className="text-sm text-taupe-500 mt-1">Todos los productos están dentro del margen mínimo configurado.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => {
            const isApplied = appliedIds.has(s.product_id);
            const isCritical =
              s.actual_margin != null && s.margin_min != null && Number(s.actual_margin) < Number(s.margin_min) - 10;
            const unit = (s.display_unit as Unit | undefined) || 'unit';
            const unitSuffix = `/${UNIT_LABEL_SHORT[unit]}`;
            const landedDisplay = toDisplay(s.landed_cost, unit);
            const baseDisplay = toDisplay(s.cost_price, unit);
            return (
              <div
                key={s.product_id}
                className={clsx(
                  'card border-l-4',
                  isApplied
                    ? 'border-emerald-400 bg-emerald-50/30'
                    : isCritical
                      ? 'border-rose-400'
                      : 'border-cream-400',
                )}
              >
                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div>
                    <h3 className="font-semibold text-taupe-900">{s.product_name}</h3>
                    <p className="text-xs text-taupe-500 mt-0.5">
                      {s.code && <span>{s.code} · </span>}
                      {s.category_name || 'Sin categoría'} · Costo final{unitSuffix}:{' '}
                      <strong>{fmtMoney(landedDisplay)}</strong>
                      {landedDisplay != null &&
                        baseDisplay != null &&
                        Number(landedDisplay) !== Number(baseDisplay) && (
                          <span className="ml-1 text-cream-700">
                            (base {fmtMoney(baseDisplay)} + extras{' '}
                            {fmtMoney(Number(landedDisplay) - Number(baseDisplay))})
                          </span>
                        )}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="text-right">
                      <p className="text-xs text-taupe-500">Margen real</p>
                      <p className={clsx('font-bold', isCritical ? 'text-rose-700' : 'text-cream-700')}>
                        {fmtPct(s.actual_margin)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-taupe-500">Mín / Obj</p>
                      <p className="font-medium text-taupe-700">
                        {fmtPct(s.margin_min)} / {fmtPct(s.margin_target)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Precio actual vs sugerido (en display_unit) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <PriceCell
                    icon={<Banknote className="h-4 w-4 text-emerald-600" />}
                    label={`Precio${unitSuffix}`}
                    current={toDisplay(s.current_cash, unit)}
                    suggested={toDisplay(s.suggested_cash, unit)}
                    edited={edits[s.product_id]?.cash}
                    onChange={(v) => handleEdit(s.product_id, 'cash', v)}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 mt-3">
                  {isApplied ? (
                    <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                      <Check className="h-4 w-4" />
                      Aplicado
                    </span>
                  ) : (
                    <Button
                      onClick={() => handleApply(s)}
                      loading={applyMutation.isPending && applyMutation.variables?.productId === s.product_id}
                    >
                      Aplicar precios
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PriceCell({
  icon,
  label,
  current,
  suggested,
  edited,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  current?: number | null;
  suggested?: number | null;
  edited?: string;
  onChange: (v: string) => void;
}) {
  const placeholder = suggested != null ? Number(suggested).toFixed(2) : '';
  return (
    <div className="bg-white border border-taupe-200 rounded-lg p-2.5">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-xs font-semibold text-taupe-700 uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xs text-taupe-500 mb-1">
        Actual: <span className="font-medium text-taupe-700">{fmtMoney(current)}</span>
      </p>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          value={edited ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          className="w-full pl-2 pr-2 py-1 text-sm border border-emerald-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
        />
      </div>
      {suggested != null && (
        <p className="text-xs text-emerald-700 mt-1">Sugerido: {fmtMoney(suggested)}</p>
      )}
    </div>
  );
}
